import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle,
  RotateCcw,
  Mail,
  Loader2,
  Undo2,
  MoreHorizontal,
  MoreVertical,
  Star,
  Printer,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ClassificationBadge } from '@/components/custom/classification-badge';
import { AssigneePicker } from '@/components/custom/assignee-picker';
import type { AssigneeValue } from '@/components/custom/assignee-picker';
import { EmailAttachmentSection } from '@/components/custom/email-attachment-section';
import type { TaskWithRelations, EmailAttachmentResponse } from '@vagusx/clients';
import { type ClassificationLabelMap, getPrimaryFromMap } from '@/hooks/use-classification-label';
import { useEmail, useEmailAttachments, useReplayEmail } from '@/hooks/use-emails';
import {
  useAssignTask,
  useUnassignTask,
  useCompleteTask,
  useMarkTaskUnread,
  useStarTask,
  useUnstarTask,
  useReopenTask,
} from '@/hooks/use-tasks';
import { useHotkey } from '@/hooks/use-hotkey';
import { cn } from '@/lib/utils';
import { deduplicateAttachments } from '@/lib/attachment-utils';
import { EmailHtmlViewer } from './EmailHtmlViewer';
import { DocumentTabContent } from './DocumentTabContent';
import { FileTabContent } from './FileTabContent';
import { LabelProvenancePopover } from './LabelProvenancePopover';
import { TaskOverviewTab } from './overview/TaskOverviewTab';

interface TaskWorkPanelProps {
  task: TaskWithRelations;
  labelMap: ClassificationLabelMap;
  /**
   * Set when the panel is hosted in a slide-over. Renders a close button at
   * the end of the toolbar, so the host can drop its own absolutely-positioned
   * one (which would sit on top of Done).
   */
  onClose?: () => void;
}

export function TaskWorkPanel({ task, labelMap, onClose }: TaskWorkPanelProps): React.ReactElement {
  const emailId = task.objectType === 'email' ? (task.objectId ?? undefined) : undefined;
  const { data: email } = useEmail(emailId);
  const { data: attachments } = useEmailAttachments(emailId);

  const completeTask = useCompleteTask();
  const reopenTask = useReopenTask();
  const markUnread = useMarkTaskUnread();
  const starTask = useStarTask();
  const unstarTask = useUnstarTask();
  const replayEmail = useReplayEmail();
  const assignTask = useAssignTask();
  const unassignTask = useUnassignTask();

  const isCompleted = task.status === 'completed';
  const isStarred = !!task.starredAt;
  const isProcessing =
    email?.processingStatus === 'pending' || email?.processingStatus === 'processing';

  // Build extraction tabs from ALL attachments that have documentId
  // Deduplicate by (emailId, attachmentIndex) to collapse Inngest retry duplicates
  const extractionTabs = useMemo(() => {
    if (!attachments) return [];
    return deduplicateAttachments(
      attachments.filter((att: EmailAttachmentResponse) => !att.contentId && att.documentId)
    ).map((att) => ({
      id: att.id,
      documentId: att.documentId!,
      fileName: att.fileName,
      labels: att.labels,
      classificationConfidence: att.classificationConfidence,
      classificationMethod: att.classificationMethod,
      classificationReason: att.classificationReason,
    }));
  }, [attachments]);

  // Build document tabs from non-inline attachments that DON'T have extractions
  // (extraction attachments get their own extraction tabs above)
  const documentTabs = useMemo(() => {
    if (!attachments) return [];
    const extractionAttIds = new Set(extractionTabs.map((t) => t.id));
    const deduped = deduplicateAttachments(
      attachments.filter(
        (att: EmailAttachmentResponse) => !att.contentId && !extractionAttIds.has(att.id)
      )
    );
    return deduped.map((att) => ({
      id: att.id,
      fileName: att.fileName,
      labels: att.labels,
      transcript: att.transcript ?? undefined,
      contentType: att.contentType,
      classificationConfidence: att.classificationConfidence,
      classificationMethod: att.classificationMethod,
      classificationReason: att.classificationReason,
    }));
  }, [attachments, extractionTabs]);

  const handleComplete = (): void => {
    completeTask.mutate(task.id, {
      onSuccess: () => toast.success('Task completed'),
    });
  };

  const handleReopen = (): void => {
    reopenTask.mutate(task.id, {
      onSuccess: () => toast.success('Task reopened'),
    });
  };

  const handleMarkUnread = (): void => {
    markUnread.mutate(task.id);
  };

  const handleToggleStar = (): void => {
    if (isStarred) {
      unstarTask.mutate(task.id);
    } else {
      starTask.mutate(task.id);
    }
  };

  const handleReplay = (): void => {
    if (!emailId) return;
    replayEmail.mutate(emailId!, {
      onSuccess: () => toast.success('Email replay triggered'),
    });
  };

  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // Controlled tab state — allows attachment clicks to switch tabs.
  // Overview is always present and is the default landing tab.
  const hasExtractions = extractionTabs.length > 0;
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Print is only meaningful on the extraction tab — the PDF tab has
  // its own print button and the email/document tabs don't have a
  // print-scoped region. Gate the action and the hotkey on activeTab.
  const isExtractionTab = activeTab.startsWith('extraction-');
  const handlePrint = useCallback((): void => {
    if (!isExtractionTab) return;
    window.print();
  }, [isExtractionTab]);

  // Global hotkeys mirror the overflow-menu items. Each hook guards
  // against typing in inputs / open dialogs (see use-hotkey.ts).
  // Mutation-pending flags gate the mutation hotkeys — otherwise a
  // user mashing `s s s` fires N toggle-star requests before the first
  // roundtrip settles. Matches the `disabled` state on the menu item.
  // Each handler also dismisses the More menu so the shortcut works
  // identically whether the menu is open or closed (the menu shows
  // these shortcut letters and the user expects them to fire).
  // Scopes the hotkeys to this panel, so a sheet hosting it (the referral
  // slide-over) doesn't count as a dialog that owns the keyboard.
  const panelRef = useRef<HTMLDivElement>(null);
  const closeMore = useCallback(() => setMoreMenuOpen(false), []);
  useHotkey(
    'p',
    () => {
      closeMore();
      handlePrint();
    },
    isExtractionTab,
    panelRef
  );
  useHotkey(
    'u',
    () => {
      closeMore();
      handleMarkUnread();
    },
    !!task.readAt && !markUnread.isPending,
    panelRef
  );
  useHotkey(
    's',
    () => {
      closeMore();
      handleToggleStar();
    },
    !starTask.isPending && !unstarTask.isPending,
    panelRef
  );
  useHotkey(
    'r',
    () => {
      closeMore();
      handleReplay();
    },
    !!emailId && !replayEmail.isPending,
    panelRef
  );
  // `m` opens the menu; closing is handled by Radix's native Escape.
  // Auto-repeat (holding `m`) calls setMoreMenuOpen(true) repeatedly,
  // which is a safe no-op once it's already true.
  useHotkey('m', () => setMoreMenuOpen(true), true, panelRef);

  // Handle attachment click: navigate to the matching tab
  const handleAttachmentClick = useCallback(
    (attachmentId: string) => {
      // If this attachment has an extraction tab, go to it
      const extTab = extractionTabs.find((t) => t.id === attachmentId);
      if (extTab) {
        setActiveTab(`extraction-${attachmentId}`);
        return;
      }
      // Otherwise go to the document tab
      setActiveTab(`doc-${attachmentId}`);
    },
    [extractionTabs]
  );

  // Tab overflow detection
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  // All tabs for the overflow dropdown. The tab badge shows the primary
  // (document_type) label from labels[] — not whatever happens to be labels[0].
  const allTabs = useMemo(() => {
    const tabs: { value: string; label: string; classification?: string | null }[] = [];
    tabs.push({ value: 'overview', label: 'Overview' });
    if (emailId) tabs.push({ value: 'email', label: 'Email' });
    for (const ext of extractionTabs) {
      tabs.push({
        value: `extraction-${ext.id}`,
        label: ext.fileName,
        classification: getPrimaryFromMap(ext.labels, labelMap),
      });
    }
    for (const doc of documentTabs) {
      tabs.push({
        value: `doc-${doc.id}`,
        label: doc.fileName,
        classification: getPrimaryFromMap(doc.labels, labelMap),
      });
    }
    return tabs;
  }, [emailId, extractionTabs, documentTabs, labelMap]);

  useEffect(() => {
    const el = tabsListRef.current;
    if (!el) return;
    const check = (): void => setHasOverflow(el.scrollWidth > el.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [allTabs]);

  return (
    <div ref={panelRef} className="relative flex h-full flex-col">
      {/* Combined tab bar + actions (single row) */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        <div className="flex h-12 items-center gap-2 border-b px-3 shrink-0">
          <div ref={tabsListRef} className="relative min-w-0 flex-1 overflow-hidden">
            {hasOverflow && (
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-background to-transparent" />
            )}
            <TabsList variant="line" className="h-full justify-start flex-nowrap">
              <TabsTrigger value="overview" className="flex-initial">
                Overview
              </TabsTrigger>
              {emailId && (
                <TabsTrigger value="email" className="flex-initial">
                  Email
                </TabsTrigger>
              )}
              {extractionTabs.map((ext) => {
                const primary = getPrimaryFromMap(ext.labels, labelMap);
                return (
                  <TabsTrigger
                    key={ext.id}
                    value={`extraction-${ext.id}`}
                    className="flex-initial gap-1.5 max-w-40"
                  >
                    <span className="truncate">{ext.fileName}</span>
                    {primary && (
                      <ClassificationBadge classification={primary} labelMap={labelMap} size="sm" />
                    )}
                  </TabsTrigger>
                );
              })}
              {documentTabs.map((doc) => {
                const primary = getPrimaryFromMap(doc.labels, labelMap);
                return (
                  <TabsTrigger
                    key={doc.id}
                    value={`doc-${doc.id}`}
                    className="flex-initial gap-1.5 max-w-40"
                  >
                    <span className="truncate">{doc.fileName}</span>
                    {primary && (
                      <ClassificationBadge classification={primary} labelMap={labelMap} size="sm" />
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {/* Overflow tab dropdown */}
          {hasOverflow && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-64 overflow-auto">
                {allTabs.map((tab) => (
                  <DropdownMenuItem
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    className={cn(
                      'flex items-center gap-1.5',
                      activeTab === tab.value && 'bg-accent font-medium'
                    )}
                  >
                    <span className="truncate">{tab.label}</span>
                    {tab.classification && (
                      <ClassificationBadge
                        classification={tab.classification}
                        labelMap={labelMap}
                        size="sm"
                      />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Assignee + Action buttons */}
          <div className="flex items-center gap-1 shrink-0">
            <AssigneePicker
              allowedTypes={['user', 'team']}
              selected={
                task.assignedToId && task.assignedToType === 'team'
                  ? {
                      type: 'team',
                      id: task.assignedToId,
                      name: task.assignedToName ?? '',
                    }
                  : task.assignedToId && task.assignedToType === 'user'
                    ? {
                        type: 'user',
                        id: task.assignedToId,
                        name: task.assignedToName ?? '',
                      }
                    : null
              }
              onChange={(value: AssigneeValue | null) => {
                if (value) {
                  // allowedTypes hides agents from the picker, so value.type is 'user' | 'team'.
                  if (value.type === 'agent') return;
                  assignTask.mutate(
                    { id: task.id, assigneeType: value.type, assigneeId: value.id },
                    { onSuccess: () => toast.success('Task assigned') }
                  );
                } else {
                  unassignTask.mutate(task.id, {
                    onSuccess: () => toast.success('Task unassigned'),
                  });
                }
              }}
              allowUnassign
              compact
              disabled={assignTask.isPending || unassignTask.isPending}
            />
            {isProcessing && (
              <Badge variant="secondary" className="text-xs">
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Processing
              </Badge>
            )}

            {/* Overflow menu — everything secondary lives here so the
             * toolbar keeps only the primary Done / Save actions +
             * assignee. Single-letter hotkeys duplicate these items so
             * keyboard users don't need to open the menu. `m` toggles
             * the menu itself. */}
            <DropdownMenu open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  title="More actions (m)"
                  aria-label="More actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onClick={handleToggleStar}
                  disabled={starTask.isPending || unstarTask.isPending}
                >
                  <Star className={cn('h-4 w-4', isStarred && 'fill-yellow-400 text-yellow-400')} />
                  <span>{isStarred ? 'Unstar' : 'Star'}</span>
                  <DropdownMenuShortcut>S</DropdownMenuShortcut>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handlePrint} disabled={!isExtractionTab}>
                  <Printer className="h-4 w-4" />
                  <span>Print</span>
                  <DropdownMenuShortcut>P</DropdownMenuShortcut>
                </DropdownMenuItem>
                {task.readAt && (
                  <DropdownMenuItem onClick={handleMarkUnread} disabled={markUnread.isPending}>
                    <Mail className="h-4 w-4" />
                    <span>Mark unread</span>
                    <DropdownMenuShortcut>U</DropdownMenuShortcut>
                  </DropdownMenuItem>
                )}
                {emailId && (
                  <DropdownMenuItem onClick={handleReplay} disabled={replayEmail.isPending}>
                    <RotateCcw className={cn('h-4 w-4', replayEmail.isPending && 'animate-spin')} />
                    <span>Re-extract</span>
                    <DropdownMenuShortcut>R</DropdownMenuShortcut>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Save extraction form (submits via form ID) */}
            {hasExtractions && (
              <SaveButton
                type="submit"
                form="doc-extraction-form"
                size="sm"
                className="h-8 px-3 text-xs"
              />
            )}

            {/* Done / Reopen */}
            {isCompleted ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-3 text-xs"
                onClick={handleReopen}
                disabled={reopenTask.isPending}
              >
                <Undo2 className="mr-1 h-3.5 w-3.5" />
                Reopen
              </Button>
            ) : (
              <Button
                size="sm"
                className="h-8 bg-green-600 px-3 text-xs text-white hover:bg-green-700"
                onClick={handleComplete}
                disabled={completeTask.isPending}
              >
                <CheckCircle className="mr-1 h-3.5 w-3.5" />
                Done
              </Button>
            )}

            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onClose}
                title="Close"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Overview tab — default landing surface for the task */}
        <TabsContent value="overview" className="relative mt-0 min-h-0 flex-1">
          <TaskOverviewTab task={task} labelMap={labelMap} />
        </TabsContent>

        {/* Email tab */}
        {emailId && (
          <TabsContent value="email" className="relative mt-0 min-h-0 flex-1">
            <div className="absolute inset-0 overflow-auto">
              {email ? (
                <>
                  <EmailHtmlViewer
                    email={email}
                    labelProvenance={
                      task.labels && task.labels.length > 0 ? (
                        <LabelProvenancePopover
                          taskLabels={task.labels}
                          emailClassification={email.classification}
                          attachments={
                            attachments
                              ?.filter((att: EmailAttachmentResponse) => !att.contentId)
                              .map((att: EmailAttachmentResponse) => ({
                                fileName: att.fileName,
                                labels: att.labels,
                                classificationConfidence: att.classificationConfidence,
                              })) ?? []
                          }
                          labelMap={labelMap}
                        />
                      ) : undefined
                    }
                  />
                  {attachments && attachments.length > 0 && (
                    <EmailAttachmentSection
                      attachments={attachments}
                      labelMap={labelMap}
                      onAttachmentClick={handleAttachmentClick}
                    />
                  )}
                </>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {/* Extraction tabs */}
        {extractionTabs.map((ext) => (
          <TabsContent
            key={ext.id}
            value={`extraction-${ext.id}`}
            className="relative mt-0 min-h-0 flex-1"
          >
            <div className="absolute inset-0 overflow-hidden">
              <DocumentTabContent
                documentId={ext.documentId}
                attachmentId={ext.id}
                attachmentLabels={ext.labels}
                classificationConfidence={ext.classificationConfidence}
                classificationMethod={ext.classificationMethod}
                classificationReason={ext.classificationReason}
                disabled={isCompleted}
              />
            </div>
          </TabsContent>
        ))}

        {/* File tabs (non-document attachments) */}
        {documentTabs.map((doc) => (
          <TabsContent
            key={doc.id}
            value={`doc-${doc.id}`}
            className="relative mt-0 min-h-0 flex-1"
          >
            <div className="absolute inset-0 overflow-hidden">
              <FileTabContent
                attachmentId={doc.id}
                fileName={doc.fileName}
                transcript={doc.transcript}
                contentType={doc.contentType}
                labels={doc.labels}
                classificationConfidence={doc.classificationConfidence}
                classificationMethod={doc.classificationMethod}
                classificationReason={doc.classificationReason}
                disabled={isCompleted}
              />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
